'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Skill {
  id: string;
  name: string;
  icon: string;
}

interface SkillCategory {
  id: string;
  title: string;
  skills: Skill[];
}

const SkillsSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const skillCategories: SkillCategory[] = [
    {
      id: 'category_core',
      title: 'Core Strengths',
      skills: [
        { id: 'skill_software_dev', name: 'Software Development', icon: 'WrenchScrewdriverIcon' },
        { id: 'skill_systems_design', name: 'Systems Design', icon: 'SquaresPlusIcon' },
        { id: 'skill_algorithms', name: 'Algorithms', icon: 'BeakerIcon' },
        { id: 'skill_java', name: 'Java', icon: 'CommandLineIcon' },
      ],
    },
    {
      id: 'category_graph',
      title: 'Graph & Data Platforms',
      skills: [
        { id: 'skill_neo4j', name: 'Neo4j', icon: 'GlobeAltIcon' },
        { id: 'skill_neptune', name: 'Amazon Neptune', icon: 'CubeIcon' },
        { id: 'skill_cosmos', name: 'Azure Cosmos DB', icon: 'SparklesIcon' },
        { id: 'skill_graph_perf', name: 'Graph Query Performance Tuning', icon: 'BoltIcon' },
      ],
    },
    {
      id: 'category_distributed',
      title: 'Distributed & Cloud Systems',
      skills: [
        { id: 'skill_distributed', name: 'Distributed Systems', icon: 'ArrowsRightLeftIcon' },
        { id: 'skill_event_driven', name: 'Event-Driven Architecture', icon: 'AdjustmentsHorizontalIcon' },
        { id: 'skill_aws', name: 'AWS', icon: 'CloudIcon' },
        { id: 'skill_azure', name: 'Azure', icon: 'CloudArrowUpIcon' },
        { id: 'skill_gcp', name: 'GCP', icon: 'CloudArrowDownIcon' },
        { id: 'skill_kubernetes', name: 'Kubernetes', icon: 'BuildingLibraryIcon' },
      ],
    },
    {
      id: 'category_delivery',
      title: 'Delivery & Tooling',
      skills: [
        { id: 'skill_node', name: 'Node.js', icon: 'ServerStackIcon' },
        { id: 'skill_spring', name: 'Spring Boot', icon: 'LeafIcon' },
        { id: 'skill_docker', name: 'Docker', icon: 'CubeTransparentIcon' },
        { id: 'skill_terraform', name: 'Infrastructure as Code', icon: 'PuzzlePieceIcon' },
        { id: 'skill_observability', name: 'CI/CD & Observability', icon: 'ChartBarIcon' },
        { id: 'skill_collaboration', name: 'Mentorship & Collaboration', icon: 'UsersIcon' },
      ],
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section data-component-id="src/app/about/components/SkillsSection.tsx:93:4" data-component-path="src/app/about/components/SkillsSection.tsx" data-component-line="93" data-component-end-line="133" data-component-file="SkillsSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-12%20border-t%20border-border%22%7D" ref={sectionRef} className="py-12 border-t border-border">
      <h2 data-component-id="src/app/about/components/SkillsSection.tsx:94:6" data-component-path="src/app/about/components/SkillsSection.tsx" data-component-line="94" data-component-end-line="100" data-component-file="SkillsSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22textContent%22%3A%22Skills%20%26%20Tools%22%7D"
        className={`text-3xl md:text-4xl font-bold tracking-tight mb-12 text-foreground transition-[opacity,transform] duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        Skills & Tools
      </h2>

      <div data-component-id="src/app/about/components/SkillsSection.tsx:102:6" data-component-path="src/app/about/components/SkillsSection.tsx" data-component-line="102" data-component-end-line="132" data-component-file="SkillsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {skillCategories.map((category, categoryIndex) => (
          <div data-component-id="src/app/about/components/SkillsSection.tsx:104:10" data-component-path="src/app/about/components/SkillsSection.tsx" data-component-line="104" data-component-end-line="130" data-component-file="SkillsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            key={category.id}
            className={`glass-card rounded-2xl p-6 transition-[opacity,transform] duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: `${categoryIndex * 0.1}s` }}
          >
            <h3 data-component-id="src/app/about/components/SkillsSection.tsx:111:12" data-component-path="src/app/about/components/SkillsSection.tsx" data-component-line="111" data-component-end-line="111" data-component-file="SkillsSection.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xl%20font-semibold%20mb-6%22%7D" className="text-xl font-semibold mb-6">{category.title}</h3>
            <div data-component-id="src/app/about/components/SkillsSection.tsx:112:12" data-component-path="src/app/about/components/SkillsSection.tsx" data-component-line="112" data-component-end-line="129" data-component-file="SkillsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20sm%3Agrid-cols-2%20gap-3%20md%3Agap-4%22%7D" className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {category.skills.map((skill) => (
                <div data-component-id="src/app/about/components/SkillsSection.tsx:114:16" data-component-path="src/app/about/components/SkillsSection.tsx" data-component-line="114" data-component-end-line="127" data-component-file="SkillsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20sm%3Agap-3%20p-2%20sm%3Ap-3%20rounded-xl%20bg-white%2F5%20hover%3Abg-white%2F10%20transition-colors%20min-w-0%22%7D"
                  key={skill.id}
                  className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors min-w-0"
                >
                  <div data-component-id="src/app/about/components/SkillsSection.tsx:118:18" data-component-path="src/app/about/components/SkillsSection.tsx" data-component-line="118" data-component-end-line="125" data-component-file="SkillsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-8%20h-8%20rounded-lg%20bg-white%2F5%20flex%20items-center%20justify-center%20flex-shrink-0%22%7D" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                    <Icon data-component-id="src/app/about/components/SkillsSection.tsx:119:20" data-component-path="src/app/about/components/SkillsSection.tsx" data-component-line="119" data-component-end-line="124" data-component-file="SkillsSection.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-primary%22%7D"
                      name={skill.icon as any}
                      size={16}
                      variant="outline"
                      className="text-primary"
                    />
                  </div>
                  <span data-component-id="src/app/about/components/SkillsSection.tsx:126:18" data-component-path="src/app/about/components/SkillsSection.tsx" data-component-line="126" data-component-end-line="126" data-component-file="SkillsSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%20truncate%22%7D" className="text-sm font-medium truncate">{skill.name}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default SkillsSection;